/**
 * Stale last-conversation pointer recovery (#5232).
 *
 * Chat message persistence is fire-and-forget: the local echo stays visible
 * even when the save fails, so a save against a conversation that no longer
 * exists on the server (HTTP 404 "Conversation not found") silently drops the
 * line on the next reload. That happens when the `xavior_last_conversation:<ws>`
 * pointer is stale — the conversation was deleted elsewhere but still passed
 * validation against a cached React-Query conversations list, so the app
 * restored (and onboarding seeded into) a dead thread.
 *
 * The recovery decision lives here as a pure function with injected
 * dependencies so it is unit-testable without the useChat hook harness
 * (mirror-function pattern). One honest recovery, no retry queue: clear the
 * stale pointer and request a conversations refetch so ConversationContext
 * re-resolves the active conversation onto a thread that actually exists.
 */
import { HttpError } from '@/lib/http'

/**
 * True when a save/load error means the target conversation no longer exists
 * on the server — the route 404'd, so the id can never become valid by
 * retrying. Everything else (network blips, 5xx, auth) is NOT a stale-pointer
 * signal.
 */
export function isConversationNotFoundError(err: unknown): boolean {
  return err instanceof HttpError && err.status === 404
}

export interface StaleConversationRecoveryDeps {
  /** The conversation id the failed save/load targeted. */
  conversationId: string
  /** Storage scope of the `xavior_last_conversation:<ws>` pointer. */
  workspaceId: string | null | undefined
  getLastViewedConversation: (workspaceId: string) => string | null
  clearLastViewedConversation: (workspaceId: string) => void
  /**
   * Trigger a conversations-list refetch so ConversationContext re-resolves
   * the active conversation against fresh server data (a deleted conversation
   * drops out of the fresh list, and the provider's data effect then falls
   * back to the workspace default conversation).
   */
  invalidateConversations: () => void
}

/**
 * Recover from a message save/load that 404'd because the conversation is
 * gone. Clears the last-conversation pointer when it still names the dead
 * conversation (never a valid pointer to a different thread) and always
 * requests a conversations refetch so the app lands on a real thread.
 *
 * Non-404 failures are not recovery cases — returns false and does nothing,
 * preserving the existing log-only behavior for transient errors.
 */
export function recoverFromStaleConversationPointer(
  err: unknown,
  deps: StaleConversationRecoveryDeps,
): boolean {
  if (!isConversationNotFoundError(err)) return false
  const { conversationId, workspaceId } = deps
  if (workspaceId && deps.getLastViewedConversation(workspaceId) === conversationId) {
    deps.clearLastViewedConversation(workspaceId)
  }
  deps.invalidateConversations()
  return true
}
